'use client'

import { useTransition } from 'react'
import { useTranslations } from 'next-intl'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Globe } from 'lucide-react'
import { updateLocalePreference } from '@/app/actions/profile'
import { locales, BETA_LOCALES, type Locale } from '@/lib/i18n/config'
import { localizedPath } from '@/lib/i18n/localized-path'
import { Badge } from '@/components/ui/badge'

const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
}

interface LocaleSwitcherProps {
  currentLocale: string
  variant?: 'compact' | 'card'
  marketingPath?: string
  /**
   * Per-locale destination map for marketing pages whose translations live at a
   * DIFFERENT slug (e.g. /alumni-management-software ↔ /es/plataforma-de-exalumnos).
   * When set, switching navigates to `marketingLanguages[newLocale]`, falling back
   * to the localized home when no twin exists. Takes precedence over marketingPath.
   */
  marketingLanguages?: Record<string, string | undefined>
}

export function LocaleSwitcher({
  currentLocale,
  variant = 'compact',
  marketingPath,
  marketingLanguages,
}: LocaleSwitcherProps) {
  const t = useTranslations('settings')
  const [isPending, startTransition] = useTransition()

  const handleChange = (newLocale: string) => {
    if (newLocale === currentLocale) return

    startTransition(async () => {
      // 1. Set cookie immediately — this is the session source of truth
      const secure = window.location.protocol === 'https:' ? ';secure' : ''
      document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax${secure}`

      // 2. Persist to DB for cross-device/cross-session (fire-and-forget)
      const beaconSent =
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(
          '/api/locale',
          new Blob([JSON.stringify({ locale: newLocale })], { type: 'application/json' })
        )

      if (!beaconSent) {
        await updateLocalePreference(newLocale).catch(() => {})
      }

      // 3. Apply: marketing pages navigate to the localized URL (each language
      // has its own indexable URL); everywhere else reload in place.
      if (marketingLanguages) {
        // Twin lives at a different slug per locale — jump to the mapped URL,
        // or the localized home when this page has no twin in the target locale.
        window.location.assign(marketingLanguages[newLocale] ?? localizedPath(newLocale, '/'))
      } else if (marketingPath) {
        window.location.assign(localizedPath(newLocale, marketingPath))
      } else {
        window.location.reload()
      }
    })
  }

  if (variant === 'card') {
    return (
      <div className="bg-white border border-border shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-medium mb-1 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {t('language')}
            </h3>
            <p className="text-sm text-muted-foreground">{t('languageDescription')}</p>
          </div>
          <Select value={currentLocale} onValueChange={handleChange} disabled={isPending}>
            <SelectTrigger aria-label={t('language')} className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {locales.map((locale) => (
                <SelectItem key={locale} value={locale}>
                  <span className="flex items-center gap-2">
                    {LOCALE_LABELS[locale]}
                    {BETA_LOCALES.includes(locale) && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 leading-4">
                        Beta
                      </Badge>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }

  // Compact variant: globe icon + small dropdown
  return (
    <Select value={currentLocale} onValueChange={handleChange} disabled={isPending}>
      <SelectTrigger
        aria-label={t('language')}
        className="w-auto gap-1.5 h-8 px-2.5 text-xs border-muted-foreground/20 bg-transparent"
      >
        <Globe className="w-3.5 h-3.5" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {locales.map((locale) => (
          <SelectItem key={locale} value={locale}>
            <span className="flex items-center gap-2">
              {LOCALE_LABELS[locale]}
              {BETA_LOCALES.includes(locale) && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 leading-4">
                  Beta
                </Badge>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
